<template>
	<div class="full-image" v-if="show">
		<div class="mask" @click="close"></div>
		<div class="image-box" @click.stop>
			<img v-show="currentLoaded" :src="currentUrl" :style="imageStyle" @click.stop @load="onImageLoad"
				@error="onImageError" />
			<div v-if="currentLoading" class="state-tip">
				<i class="el-icon-loading"></i>
				<span>{{ $t('common.loading') }}</span>
			</div>
			<div v-else-if="currentLoadError" class="state-tip">{{ $t('chat.imageLost') }}</div>
		</div>
		<button class="close-btn" type="button" :title="$t('common.close')" @click.stop="close">
			<i class="el-icon-close"></i>
		</button>
		<div class="toolbar" @click.stop>
			<button class="tool-btn" type="button" :disabled="!hasPrev" @click="move(-1)">
				<i class="el-icon-arrow-left"></i>
			</button>
			<button class="tool-btn" type="button" :disabled="!canZoomOut" @click="zoomOut">
				<i class="el-icon-zoom-out"></i>
			</button>
			<button class="tool-btn" type="button" :disabled="!canZoomIn" @click="zoomIn">
				<i class="el-icon-zoom-in"></i>
			</button>
			<button class="tool-btn" type="button" :disabled="!currentLoaded" @click="rotate">
				<i class="el-icon-refresh-right"></i>
			</button>
			<button class="tool-btn" type="button" :disabled="!currentLoaded" :title="$t('chat.download')"
				@click="download">
				<i class="el-icon-download"></i>
			</button>
			<button v-if="canLocate" class="tool-btn" type="button" :title="$t('chat.locateInChat')" @click="locate">
				<i class="el-icon-aim"></i>
			</button>
			<button class="tool-btn" type="button" :disabled="!hasNext" @click="move(1)">
				<i class="el-icon-arrow-right"></i>
			</button>
		</div>
	</div>
</template>

<script>
import { MESSAGE_TYPE, MESSAGE_STATUS } from '../../api/enums.js';

const PAGE_SIZE = 300;
const SCALE_STEP = 0.25;
const SCALE_MIN = 0.5;
const SCALE_MAX = 3;

export default {
	name: "fullImage",
	data() {
		return {
			show: false,
			items: [],
			index: 0,
			scale: 1,
			rotateDeg: 0,
			convKey: '',
			convMinSeqNo: 1,
			convMaxSeqNo: 0,
			scannedMinSeqNo: 0,
			scannedMaxSeqNo: 0,
			noMoreOlder: true,
			noMoreNewer: true,
			downloading: false
		}
	},
	computed: {
		currentUrl() {
			return (this.items[this.index] && this.items[this.index].url) || '';
		},
		currentLoaded() {
			const item = this.items[this.index];
			return !!(item && item.loaded);
		},
		currentLoading() {
			const item = this.items[this.index];
			return !!(item && !item.loaded && !item.loadError);
		},
		currentLoadError() {
			const item = this.items[this.index];
			return !!(item && item.loadError);
		},
		imageStyle() {
			return {
				transform: `translate(-50%, -50%) scale(${this.scale}) rotate(${this.rotateDeg}deg)`
			};
		},
		hasPrev() {
			return this.index > 0 || !this.noMoreOlder;
		},
		hasNext() {
			return this.index < this.items.length - 1 || !this.noMoreNewer;
		},
		canZoomIn() {
			return this.currentLoaded && this.scale < SCALE_MAX;
		},
		canZoomOut() {
			return this.currentLoaded && this.scale > SCALE_MIN;
		},
		canLocate() {
			const item = this.items[this.index];
			return !!(item && item.localId);
		}
	},
	methods: {
		async open(payload) {
			this.reset();
			if (typeof payload === 'string') {
				this.items = payload ? [{ url: payload, seqNo: 0 }] : [];
			} else if (payload && payload.url && payload.convKey && payload.seqNo) {
				await this.openFromConversation(payload);
				return;
			} else if (payload && payload.url) {
				this.items = [{
					url: payload.url,
					seqNo: payload.seqNo || 0,
					localId: payload.localId || ''
				}];
			}
			if (!this.items.length) {
				return;
			}
			this.show = true;
			this.bindKey();
		},
		async openFromConversation({ convKey, url, seqNo, localId }) {
			const conv = await this.$db.findConversationByKey(convKey);
			this.convKey = convKey;
			this.convMinSeqNo = conv.minSeqNo;
			this.convMaxSeqNo = conv.maxSeqNo;
			this.items = [{ url, seqNo, localId: localId || '' }];
			this.index = 0;
			this.scannedMinSeqNo = seqNo;
			this.scannedMaxSeqNo = seqNo;
			this.noMoreOlder = false;
			this.noMoreNewer = false;
			this.show = true;
			this.bindKey();
			// 打开时双向预载
			await Promise.all([this.loadMore(true), this.loadMore(false)]);
		},
		extractImages(messages) {
			const list = [];
			messages.forEach(m => {
				if (m.deleted || m.type !== MESSAGE_TYPE.IMAGE || m.status === MESSAGE_STATUS.RECALL || !m.seqNo) {
					return;
				}
				const url = JSON.parse(m.content).originUrl;
				list.push({ url, seqNo: m.seqNo, localId: m.localId });
			});
			return list.sort((a, b) => a.seqNo - b.seqNo);
		},
		// 将本页图片并入列表，有新增返回 true
		mergeImages(messages, isOlder) {
			const exist = new Set(this.items.map(i => i.seqNo));
			const images = this.extractImages(messages).filter(i => !exist.has(i.seqNo));
			if (!images.length) {
				return false;
			}
			if (isOlder) {
				this.items = images.concat(this.items);
				this.index += images.length;
			} else {
				this.items = this.items.concat(images);
			}
			return true;
		},
		/**
		 * 按消息页向更早/更新方向扫描，直到找到图片或到达会话边界
		 * @param {boolean} isOlder true=更早的消息
		 */
		async loadMore(isOlder) {
			if (!this.convKey) {
				return;
			}
			if (isOlder) {
				if (this.noMoreOlder) {
					return;
				}
				let cursor = this.scannedMinSeqNo - 1;
				while (cursor >= this.convMinSeqNo) {
					const min = Math.max(this.convMinSeqNo, cursor - PAGE_SIZE + 1);
					const messages = await this.$db.findPageMessage(this.convKey, min, cursor);
					this.scannedMinSeqNo = min;
					if (this.mergeImages(messages, true) || min <= this.convMinSeqNo) {
						break;
					}
					cursor = min - 1;
				}
				if (this.scannedMinSeqNo <= this.convMinSeqNo) {
					this.noMoreOlder = true;
				}
			} else {
				if (this.noMoreNewer) {
					return;
				}
				let cursor = this.scannedMaxSeqNo + 1;
				while (cursor <= this.convMaxSeqNo) {
					const max = Math.min(this.convMaxSeqNo, cursor + PAGE_SIZE - 1);
					const messages = await this.$db.findPageMessage(this.convKey, cursor, max);
					this.scannedMaxSeqNo = max;
					if (this.mergeImages(messages, false) || max >= this.convMaxSeqNo) {
						break;
					}
					cursor = max + 1;
				}
				if (this.scannedMaxSeqNo >= this.convMaxSeqNo) {
					this.noMoreNewer = true;
				}
			}
		},
		async move(step) {
			const nextIdx = this.index + step;
			if (nextIdx >= 0 && nextIdx < this.items.length) {
				this.index = nextIdx;
				this.resetTransform();
				return;
			}
			// 已到列表边缘，继续按消息页补载
			if (step < 0) {
				if (this.noMoreOlder) {
					return;
				}
				await this.loadMore(true);
			} else {
				if (this.noMoreNewer) {
					return;
				}
				await this.loadMore(false);
			}
			const idx = this.index + step;
			if (idx >= 0 && idx < this.items.length) {
				this.index = idx;
				this.resetTransform();
			}
		},
		onImageLoad() {
			const item = this.items[this.index];
			if (item) {
				this.$set(item, 'loaded', true);
			}
		},
		onImageError() {
			const item = this.items[this.index];
			if (item) {
				this.$set(item, 'loadError', true);
				this.$set(item, 'loaded', false);
			}
		},
		zoomIn() {
			this.scale = Math.min(SCALE_MAX, +(this.scale + SCALE_STEP).toFixed(2));
		},
		zoomOut() {
			this.scale = Math.max(SCALE_MIN, +(this.scale - SCALE_STEP).toFixed(2));
		},
		rotate() {
			this.rotateDeg = (this.rotateDeg + 90) % 360;
		},
		resetTransform() {
			this.scale = 1;
			this.rotateDeg = 0;
		},
		async locate() {
			const item = this.items[this.index];
			if (!item || !item.localId) {
				return;
			}
			const message = await this.$db.findMessageByLocalId(item.localId);
			if (!message || message.deleted || message.status === MESSAGE_STATUS.RECALL) {
				this.$message.error(this.$t('chat.locateFail'));
				return;
			}
			const convKey = message.convKey || this.convKey;
			this.close();
			if (this.chatStore.isActive(convKey)) {
				this.$eventBus.$emit('locateChatMessage', message);
				return;
			}
			if (!this.chatStore.conversationMap.get(convKey)) {
				this.$message.error(this.$t('chat.locateFail'));
				return;
			}
			this.chatStore.setPendingLocateMessage(message);
			await this.chatStore.moveTop(convKey);
			this.chatStore.setActive(convKey);
			this.$router.push('/home/chat');
		},
		async download() {
			if (!this.currentLoaded) {
				return;
			}
			const url = this.currentUrl;
			if (!url || this.downloading) {
				return;
			}
			this.downloading = true;
			const fileName = this.genFileName(url);
			try {
				const res = await fetch(url);
				if (!res.ok) {
					throw new Error('fetch failed');
				}
				const blob = await res.blob();
				const objectUrl = URL.createObjectURL(blob);
				this.triggerDownload(objectUrl, fileName);
				URL.revokeObjectURL(objectUrl);
			} catch (e) {
				this.triggerDownload(url, fileName);
			} finally {
				this.downloading = false;
			}
		},
		triggerDownload(href, fileName) {
			const a = document.createElement('a');
			a.href = href;
			a.download = fileName;
			a.target = '_blank';
			a.rel = 'noopener';
			document.body.appendChild(a);
			a.click();
			document.body.removeChild(a);
		},
		genFileName(url) {
			try {
				const path = url.split('?')[0];
				const name = path.substring(path.lastIndexOf('/') + 1);
				if (name && /\.(png|jpe?g|gif|webp|bmp)$/i.test(name)) {
					return name;
				}
			} catch (e) {
				// ignore
			}
			return `image_${Date.now()}.jpg`;
		},
		onKeydown(e) {
			if (!this.show) {
				return;
			}
			if (e.key === 'Escape') {
				this.close();
			} else if (e.key === 'ArrowLeft') {
				e.preventDefault();
				this.move(-1);
			} else if (e.key === 'ArrowRight') {
				e.preventDefault();
				this.move(1);
			}
		},
		bindKey() {
			document.addEventListener('keydown', this.onKeydown);
		},
		unbindKey() {
			document.removeEventListener('keydown', this.onKeydown);
		},
		reset() {
			this.unbindKey();
			this.items = [];
			this.index = 0;
			this.scale = 1;
			this.rotateDeg = 0;
			this.convKey = '';
			this.convMinSeqNo = 1;
			this.convMaxSeqNo = 0;
			this.scannedMinSeqNo = 0;
			this.scannedMaxSeqNo = 0;
			this.noMoreOlder = true;
			this.noMoreNewer = true;
			this.downloading = false;
		},
		close() {
			this.show = false;
			this.reset();
		}
	},
	beforeDestroy() {
		this.unbindKey();
	}
}
</script>

<style lang="scss">
.full-image {
	position: fixed;
	inset: 0;
	z-index: 9999;

	.mask {
		position: absolute;
		inset: 0;
		background: rgba(0, 0, 0, 0.85);
	}

	.image-box {
		position: relative;
		width: 100%;
		height: 100%;
		pointer-events: none;
		overflow: hidden;

		img {
			position: absolute;
			left: 50%;
			top: 50%;
			transform: translate(-50%, -50%);
			max-height: calc(100% - 80px);
			max-width: calc(100% - 40px);
			pointer-events: auto;
			user-select: none;
			transition: transform 0.15s ease;
			transform-origin: center center;
		}

		.state-tip {
			position: absolute;
			left: 50%;
			top: 50%;
			transform: translate(-50%, -50%);
			display: flex;
			flex-direction: column;
			align-items: center;
			gap: 10px;
			color: rgba(255, 255, 255, 0.65);
			font-size: 15px;
			pointer-events: none;
			user-select: none;

			.el-icon-loading {
				font-size: 28px;
				color: rgba(255, 255, 255, 0.85);
			}
		}
	}

	.close-btn {
		position: fixed;
		top: 20px;
		right: 20px;
		width: 40px;
		height: 40px;
		border: none;
		border-radius: 50%;
		background: #333;
		opacity: 0.5;
		color: #fff;
		cursor: pointer;
		z-index: 2;
		display: flex;
		align-items: center;
		justify-content: center;
		font-size: 20px;

		&:hover {
			opacity: 0.8;
		}
	}

	.toolbar {
		position: fixed;
		left: 50%;
		bottom: 28px;
		transform: translateX(-50%);
		display: flex;
		align-items: center;
		gap: 6px;
		padding: 8px 14px;
		background: rgba(0, 0, 0, 0.55);
		border-radius: 24px;
		z-index: 2;

		.tool-btn {
			width: 36px;
			height: 36px;
			border: none;
			border-radius: 50%;
			background: transparent;
			color: #fff;
			cursor: pointer;
			display: flex;
			align-items: center;
			justify-content: center;
			font-size: 18px;

			&:hover:not(:disabled) {
				background: rgba(255, 255, 255, 0.15);
			}

			&:disabled {
				opacity: 0.3;
				cursor: default;
			}
		}
	}
}
</style>
